/**
 * Reading only the products that changed (migration 114, 2026-09-26).
 *
 * Every sale on any device used to make every open device re-read the whole
 * catalogue — at 10 000 products, 490 KB compressed each time. Now a device
 * remembers when it last read (the server's clock, never the device's) and
 * asks `list_products_changed` for what moved since. The rules for folding
 * the answer into the list live here, once, where they can be tested.
 */
import type { Product } from './mock-data'

/** Ask from a little before the last answer: `updated_at` is the transaction's
 *  START, so a sale that commits a moment after we read carries an earlier
 *  stamp. Two minutes covers any transaction this app makes; merging by id
 *  makes the overlap free. */
export const OVERLAP_MS = 2 * 60_000

/** More changes than this come back as „read everything" (114's LIMIT 1001). */
export const MAX_CHANGED = 1000

export function sinceFor(watermark: string): string {
  return new Date(Date.parse(watermark) - OVERLAP_MS).toISOString()
}

/** The newest `updated_at` in a full read — where the next small read starts. */
export function newestStamp(rows: { updated_at?: string | null }[]): string | null {
  let best: string | null = null
  let bestMs = -Infinity
  for (const r of rows) {
    const ms = r.updated_at ? Date.parse(r.updated_at) : NaN
    if (Number.isFinite(ms) && ms > bestMs) { bestMs = ms; best = r.updated_at! }
  }
  return best
}

/** The list's order, the same as list_products: newest first, id as the tiebreaker. */
function byNewest(a: Product, b: Product): number {
  if (a.created_at !== b.created_at) return a.created_at < b.created_at ? 1 : -1
  return a.id < b.id ? 1 : a.id > b.id ? -1 : 0
}

/**
 * The list with the changed products in it: each replaces its old self in
 * place, a new one takes its place by the list's order. Nothing changed →
 * the SAME array, so nothing downstream re-renders or re-saves.
 */
export function mergeChanged(current: Product[], changed: Product[]): Product[] {
  if (changed.length === 0) return current
  const byId = new Map(changed.map((p) => [p.id, p]))
  const next = current.map((p) => {
    const c = byId.get(p.id)
    if (!c) return p
    byId.delete(p.id)
    return c
  })
  if (byId.size === 0) return next
  return [...next, ...byId.values()].sort(byNewest)
}
